<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="project-dashboard">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb" :items="headerItems">
        <template #title>
          <div class="row middle">
            <h1 class="header__title">{{ $metaInfo.title }}</h1>

            <img
              v-if="($store.state.enums.UsedProjectLanguages || []).length > 1"
              class="ml-2"
              :src="`/images/flags/${item.metadata.language}.svg`"
              width="20"
            />
            <ButtonComponent
              tag="router-link"
              :disabled="!$store.getters['Project/hasRole']('manager')"
              :to="{ name: 'project-edit', params: { id, step: 'publishing' } }"
              :label="labels[item.isPublished ? 'published' : 'unpublished']"
              :icon="{ icon: ['fas', 'circle'], class: item.isPublished ? 'text--success' : 'text--error' }"
              icon-placement="left"
              class="project-dashboard__published"
            />
          </div>
        </template>
      </HeaderComponent>
      <div class="project-dashboard__content">
        <Tasks
          data-field="tasks"
          :value="item.tasks"
          :reference="{ onModel: 'Project', ref: id }"
          :show-create="false"
        />
        <Deadlines data-field="deadlines" :value="item.deadlines" />
        <Statistics :addons="addons" data-field="statistics" />
        <Units :addons="addons" data-field="units" />
        <Card :title="$t('recentActivity')" class="recent-activity-widget">
          <template #default>
            <LastActivity data-field="last-activity" max-height="550px" :value="item.lastActivity" />
          </template>
        </Card>
      </div>
    </div>

    <RemoveComponent
      :show.sync="removeDialog"
      :title="item.name"
      :action="() => remove({ id })"
      @onSuccess="
        $router.push({
          name: 'projects',
        })
      "
      :resource="$tc('project')"
    />
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mapi18n } from '@/i18n'
import Bus from '@/bus'
import { Nest } from '@/components/Flatfinder/utils'
import ProjectApi from '@/api/Project'

import Remove from '@/components/Remove'
import RemoveComponent from '@/components/Project/Remove'
import Units from '@/components/Project/Dashboard/Units'
import Statistics from '@/components/Project/Dashboard/Statistics'

import HeaderComponent from '@/components/Header'
import Deadlines from '@/components/Deadlines/Widget'
import LastActivity from '@/components/LastActivity/Widget'
import Card from '@/components/Card'
import Tasks from '@/components/Task/Widget'

import EditSteps from './Mutate/Steps'

export default {
  props: {
    id: String,
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      removeDialog: false,
    }
  },
  computed: {
    ...mapState('Project', ['item', 'promises']),
    ...mapi18n('labels', {
      projects: ['project', 2],
      edit: 'edit',
      published: 'published',
      unpublished: 'unpublished',
      loading() {
        return this.$t('loading', { resource: this.$tc('project', 1).toLowerCase() }) + '...'
      },
    }),
    addons() {
      return this.$path('metadata.addons', this.item) || []
    },
    breadcrumb() {
      return [{ title: this.labels.projects, action: { name: 'projects' } }, { title: this.$metaInfo.title }]
    },
    editSteps() {
      return EditSteps(this.item).filter(e => {
        switch (e.id) {
          case 'shops':
            return (
              this.$store.getters['Project/hasRole'](/^shop/) &&
              !this.$store.state.enums.DisabledFeatures.includes('project:shop')
            )
          case 'sale':
            return !this.$store.state.enums.DisabledFeatures.includes('project:bid')
          default:
            return true
        }
      })
    },
    reference() {
      return {
        on: this.id,
        onModel: 'Project',
      }
    },
    canCreateFlatfinder() {
      return ['project:presentation', 'project:flatfinder'].some(a => this.addons.includes(a))
    },
    flatfinders() {
      return Nest(this.item.flatfinders)
    },
    headerItems() {
      return [
        {
          component: 'DropdownComponent',
          props: {
            'close-on-click': false,
          },
          children: {
            default: [
              {
                component: 'ButtonComponent',
                class: '',
                props: {
                  label: this.$t('show'),
                  icon: ['fal', 'ellipsis-v'],
                },
              },
            ],
            dropdown: [
              {
                component: 'ButtonComponent',
                props: {
                  tag: 'router-link',
                  label: this.$tc('lead', 2),
                  icon: ['fal', 'users'],
                  to: {
                    name: 'project-leads',
                    params: { id: this.id },
                  },
                },
              },
              {
                component: 'ButtonComponent',
                condition: this.$store.getters['Project/hasRole'](['manager', 'salesman']),
                props: {
                  tag: 'router-link',
                  label: this.$tc('openhouse', 2),
                  icon: ['fal', 'sign'],
                  to: {
                    name: 'project-openhouse',
                    params: { id: this.id },
                  },
                },
              },
              {
                component: 'ButtonComponent',
                condition: this.$store.getters['Project/hasRole'](['signingAuthority', 'shop.signingAuthority']),
                props: {
                  tag: 'router-link',
                  label: this.$tc('document', 2),
                  icon: ['fal', 'file-contract'],
                  to: {
                    name: 'project-contracts',
                    params: { id: this.id },
                  },
                },
              },

              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'project:show',
                  query: { project: this.id },
                },
              },

              {
                component: 'ButtonComponent',
                condition:
                  ['project:complaint'].some(el => this.addons.includes(el)) &&
                  this.$store.getters['Project/hasRole']('complaint.manager', this.item),
                props: {
                  tag: 'router-link',
                  label: this.$tc('complaint', 2),
                  icon: ['far', 'list-ul'],
                  to: {
                    name: 'project-complaints',
                    params: { id: this.id },
                  },
                },
              },
              {
                component: 'ButtonComponent',
                props: {
                  tag: 'a',
                  href: this.item.url,
                  target: '_blank',
                  label: this.$t('projectsite'),
                  icon: ['fal', 'eye'],
                },
              },
            ],
          },
        },

        {
          component: 'Actions',
          props: {
            condition: this.$store.getters.hasAccess('admin'),
            placement: 'project',
            query: { project: this.id },
            'use-portal': false,
          },
        },

        {
          component: 'DropdownComponent',
          condition: this.$store.getters['Project/hasRole']('manager'),
          props: {
            'close-on-click': false,
          },
          children: {
            default: [
              {
                component: 'ButtonComponent',
                class: '',
                props: {
                  label: this.labels.edit,
                  icon: ['fal', 'pen'],
                },
              },
            ],
            dropdown: [
              ...this.editSteps
                .filter(s => {
                  switch (s.id) {
                    case 'shops':
                      return this.$store.getters['Project/hasRole'](/^shop/)
                    case 'sale':
                      return !this.$store.state.enums.DisabledFeatures.includes('project:bid')
                    default:
                      return true
                  }
                })
                .map(i => {
                  return {
                    component: 'ButtonComponent',
                    props: {
                      tag: 'router-link',
                      label: i.label,
                      icon: i.icon,
                      to: {
                        name: 'project-edit',
                        params: { id: this.id, step: i.id },
                      },
                    },
                  }
                }),
              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'project:edit',
                  query: { project: this.id },
                },
              },
            ],
          },
        },

        {
          component: 'ButtonComponent',
          condition: this.$store.getters['Project/hasRole']('manager'),
          props: {
            tag: 'router-link',
            label: this.$t('analytics'),
            icon: ['fal', 'analytics'],
            to: {
              name: 'project-analytics',
              params: { id: this.id },
            },
          },
        },

        {
          component: 'DropdownComponent',
          condition: this.$store.getters['Project/hasRole'](['manager', 'salesman']),
          props: {
            'close-on-click': false,
          },
          children: {
            default: [
              {
                component: 'ButtonComponent',
                class: '',
                props: {
                  label: this.$t('add'),
                  icon: ['fal', 'plus'],
                  theme: 'primary',
                },
              },
            ],
            dropdown: [
              {
                component: 'ButtonComponent',
                condition: this.$store.getters['Project/hasRole']('manager'),
                props: {
                  label: this.$tc('unit'),
                  icon: ['fal', 'home'],
                },
                listners: {
                  click: () => this.createResidential(),
                },
              },
              {
                component: 'ButtonComponent',
                condition: this.$store.getters['Project/hasRole']('manager') && this.$glob(this.item.type, '!singular'),
                props: {
                  label: this.$tc('unitSelector'),
                  icon: ['fal', 'layer-plus'],

                  tag: 'router-link',
                  to: this.canCreateFlatfinder
                    ? { name: 'project-flatfinder-create', params: { project: this.item.id } }
                    : { name: 'project-edit', params: { id: this.id, step: 'flatfinders' } },
                },
              },

              {
                component: 'ButtonComponent',

                props: {
                  'v-tooltip.right': {
                    content:
                      this.$path('item.metadata.disabledFields.project.openhouses') ||
                      this.$path('item.metadata.disabledFields.unit.openhouses'),
                    boundariesElement: this.$body,
                  },
                  class: {
                    'elder-button--disabled':
                      this.$path('item.metadata.disabledFields.project.openhouses') ||
                      this.$path('item.metadata.disabledFields.unit.openhouses'),
                  },
                  label: this.$tc('openhouse'),
                  icon: ['fal', 'sign'],

                  tag:
                    this.$path('item.metadata.disabledFields.project.openhouses') ||
                    this.$path('item.metadata.disabledFields.unit.openhouses')
                      ? 'div'
                      : 'router-link',
                  to: {
                    name: 'project-openhouse-create',
                    params: { project: this.id },
                    query: { returnTo: this.$route.path },
                  },
                },
              },

              {
                component: 'ButtonComponent',
                props: {
                  label: this.$tc('lead'),
                  icon: ['fal', 'user'],
                  tag: 'router-link',
                  to: {
                    name: 'project-lead-create',
                    params: { reference: this.id },
                    query: { returnTo: this.$route.path },
                  },
                },
              },
              {
                component: 'ButtonComponent',
                condition: this.$store.getters.isAdmin,
                props: {
                  label: this.$tc('integration'),
                  icon: ['fal', 'puzzle-piece'],
                  tag: 'router-link',
                  to: {
                    name: 'project-edit',
                    params: { project: this.id, step: 'integrations' },
                  },
                },
              },
              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'project:add',
                  query: { project: this.id },
                },
              },
            ],
          },
        },
      ]
    },
  },
  methods: {
    ...mapActions('Project', {
      fetch(dispatch) {
        return dispatch('fetch', [{ id: this.id }, { query: 'ProjectDashboard' }]).then(data => {
          if (!data) return this.$router.push({ name: 'notfound' })
        })
      },
      mutate: 'mutate',
      remove: 'remove',
    }),
    async createResidential() {
      await Bus.$emit('tab:set', 'units')
      Bus.$emit('residentials:create', true)
    },
  },
  created() {
    this.$bus.$on('task:update', this.fetch)
    this.$bus.$on('task:remove', this.fetch)
  },
  beforeDestroy() {
    this.$bus.$off('task:update', this.fetch)
    this.$bus.$off('task:remove', this.fetch)
  },
  metaInfo() {
    return {
      title: this.item.name || this.labels.loading,
    }
  },
  components: {
    Card,
    HeaderComponent,

    Deadlines,
    LastActivity,
    Tasks,
    RemoveComponent,
    Units,
    Statistics,
    Remove,
  },
}
</script>

<style lang="scss">
.project-dashboard {
  background-color: $background-grey;

  &__published {
    margin-left: 1rem;
    font-size: 0.65em;
  }

  &__flatfinders {
    &--nested {
      .elder-button__label:before {
        content: 'L';
        opacity: 0.2;
        margin-right: 0.5em;
      }
    }
  }

  &__content {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 480px 600px;
    grid-gap: 2rem;

    @include respond-below('phone') {
      grid-template-columns: 1fr;
    }

    & > * {
      animation: fadeIn 300ms ease;
    }
  }

  .elder-button__default-tag {
    padding-right: var(--vue-elder-button-padding-x, 1.5em);
  }

  label[for='statistics-selector'] {
    margin-left: auto;
  }

  [data-field='units'] {
    grid-column-end: span 2;
    @include respond-below('phone') {
      grid-column-end: initial;
    }
  }

  .tabs {
    &__details {
      border: none !important;
    }

    &__header {
      ul,
      a {
        border: none !important;
      }

      a {
        background-color: rgba(black, 0.025);
      }

      &--active a {
        background-color: White;
      }
    }

    &__tab {
      padding: 0 !important;
    }
  }
}
</style>
